import { Delete as DeleteIcon } from "@mui/icons-material";
import { FormControl, IconButton, MenuItem, TextField } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";
import SearchableSelect from "./SearchableSelect";
import { safeDisplay } from "./sharedDropdownOptions";

// Common Cell Components
export const ChangeEventCell = ({ row, handleEdit, dropdownOptions, getDisplayText }) => (
  <FormControl size="small" sx={{ minWidth: 220 }}>
    <SearchableSelect
      value={row.original.changeEvent || ""}
      displayEmpty
      onChange={(e) => handleEdit(row.original.id, "changeEvent", e.target.value)}
      renderValue={(v) =>
        v === "" ? (
          <span style={{ color: "#9e9e9e" }}>Select Change Event</span>
        ) : (
          getDisplayText(v, dropdownOptions?.changeEvents, "changeEvent")
        )
      }
    >
      <MenuItem value="" disabled>
        <em>Select Change Event</em>
      </MenuItem>
      {dropdownOptions?.changeEvents?.map((ce) => (
        <MenuItem key={ce.id} value={ce.id}>
          {ce.number ? `${ce.number} — ${safeDisplay(ce.title)}` : safeDisplay(ce.title)}
        </MenuItem>
      ))}
    </SearchableSelect>
  </FormControl>
);

ChangeEventCell.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
  dropdownOptions: PropTypes.object.isRequired,
  getDisplayText: PropTypes.func.isRequired,
};

export const BudgetCodeCell = ({ row, handleEdit, dropdownOptions, getDisplayText }) => (
  <FormControl size="small" sx={{ minWidth: 220 }}>
    <SearchableSelect
      value={row.original.budgetCodeId || ""}
      displayEmpty
      onChange={(e) => handleEdit(row.original.id, "budgetCodeId", e.target.value)}
      renderValue={(v) =>
        v !== "" ? (
          getDisplayText(v, dropdownOptions?.budgetCodes, "budgetCode")
        ) : (
          <span style={{ color: "#9e9e9e" }}>Select Budget Code</span>
        )
      }
    >
      <MenuItem value="" disabled>
        <em>Select Budget Code</em>
      </MenuItem>
      {dropdownOptions?.budgetCodes?.map((b) => (
        <MenuItem key={b.id} value={b.id}>
          {b.code ? `${b.code} — ${safeDisplay(b.name)}` : safeDisplay(b.name)}
        </MenuItem>
      ))}
    </SearchableSelect>
  </FormControl>
);

BudgetCodeCell.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
  dropdownOptions: PropTypes.object.isRequired,
  getDisplayText: PropTypes.func.isRequired,
};

export const DescriptionCell = ({ row, handleEdit }) => (
  <TextField
    value={row.original.description || ""}
    onChange={(e) => handleEdit(row.original.id, "description", e.target.value)}
    size="small"
    fullWidth
    placeholder="Enter Description"
  />
);

DescriptionCell.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
};

export const AmountCell = ({ row, handleEdit }) => (
  <TextField
    value={row.original.amount || ""}
    onChange={(e) => handleEdit(row.original.id, "amount", e.target.value)}
    size="small"
    fullWidth
    placeholder="$0.00"
    inputProps={{
      step: "0.01",
      min: 0,
      pattern: String.raw`\d*(\.\d{0,})?`,
      style: { textAlign: "right" },
    }}
  />
);

AmountCell.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
};

export const BilledCell = ({ row, handleEdit }) => (
  <TextField
    value={row.original.billed || ""}
    onChange={(e) => handleEdit(row.original.id, "billed", e.target.value)}
    size="small"
    fullWidth
    placeholder="$0.00"
    inputProps={{
      step: "0.01",
      min: 0,
      pattern: String.raw`\d*(\.\d{0,})?`,
      style: { textAlign: "right" },
    }}
  />
);

BilledCell.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
};

export const RemainingCell = ({ row, handleEdit }) => (
  <TextField
    value={row.original.remaining || ""}
    onChange={(e) => handleEdit(row.original.id, "remaining", e.target.value)}
    size="small"
    fullWidth
    placeholder="$0.00"
    inputProps={{
      step: "0.01",
      min: 0,
      pattern: String.raw`\d*(\.\d{0,})?`,
      style: { textAlign: "right" },
    }}
  />
);

RemainingCell.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
};

export const ActionsCell = ({ row, handleRemoveRow }) => (
  <IconButton size="small" onClick={() => handleRemoveRow(row.original.id)} color="error">
    <DeleteIcon fontSize="small" />
  </IconButton>
);

ActionsCell.propTypes = {
  row: PropTypes.object.isRequired,
  handleRemoveRow: PropTypes.func.isRequired,
};

// Purchase Order specific cells
export const QtyCell = ({ row, handleEdit }) => (
  <TextField
    value={row.original.qty || ""}
    onChange={(e) => handleEdit(row.original.id, "qty", e.target.value)}
    size="small"
    fullWidth
    placeholder="Enter Qty"
    inputProps={{
      step: "0.01",
      min: 0,
      pattern: String.raw`\d*(\.\d{0,})?`,
      style: { textAlign: "right" },
    }}
  />
);

QtyCell.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
};

export const UomCell = ({ row, handleEdit, dropdownOptions, getDisplayText }) => (
  <FormControl size="small" sx={{ minWidth: 120 }}>
    <SearchableSelect
      value={row.original.uom || ""}
      displayEmpty
      onChange={(e) => handleEdit(row.original.id, "uom", e.target.value)}
      renderValue={(v) =>
        v === "" ? (
          <span style={{ color: "#9e9e9e" }}>Select UOM</span>
        ) : (
          getDisplayText(v, dropdownOptions?.unitOfMeasure, "unitOfMeasure")
        )
      }
    >
      <MenuItem value="" disabled>
        <em>Select UOM</em>
      </MenuItem>
      {dropdownOptions?.uom?.map((u) => (
        <MenuItem key={u.value} value={u.value}>
          {safeDisplay(u.name)}
        </MenuItem>
      ))}
    </SearchableSelect>
  </FormControl>
);

UomCell.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
  dropdownOptions: PropTypes.object.isRequired,
  getDisplayText: PropTypes.func.isRequired,
};

export const UnitCostCell = ({ row, handleEdit }) => (
  <TextField
    value={row.original.unitCost || ""}
    onChange={(e) => handleEdit(row.original.id, "unitCost", e.target.value)}
    size="small"
    fullWidth
    placeholder="$0.00"
    inputProps={{
      step: "0.01",
      min: 0,
      pattern: String.raw`\d*(\.\d{0,})?`,
      style: { textAlign: "right" },
    }}
  />
);

UnitCostCell.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
};

// Wrapper components for inline Cell functions to satisfy PropTypes validation
export const ChangeEventCellWrapper = ({ row, handleEdit, dropdownOptions, getDisplayText }) => (
  <ChangeEventCell
    row={row}
    handleEdit={handleEdit}
    dropdownOptions={dropdownOptions}
    getDisplayText={getDisplayText}
  />
);

ChangeEventCellWrapper.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
  dropdownOptions: PropTypes.object.isRequired,
  getDisplayText: PropTypes.func.isRequired,
};

export const BudgetCodeCellWrapper = ({ row, handleEdit, dropdownOptions, getDisplayText }) => (
  <BudgetCodeCell
    row={row}
    handleEdit={handleEdit}
    dropdownOptions={dropdownOptions}
    getDisplayText={getDisplayText}
  />
);

BudgetCodeCellWrapper.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
  dropdownOptions: PropTypes.object.isRequired,
  getDisplayText: PropTypes.func.isRequired,
};

export const DescriptionCellWrapper = ({ row, handleEdit }) => (
  <DescriptionCell row={row} handleEdit={handleEdit} />
);

DescriptionCellWrapper.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
};

export const AmountCellWrapper = ({ row, handleEdit }) => (
  <AmountCell row={row} handleEdit={handleEdit} />
);

AmountCellWrapper.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
};

export const BilledCellWrapper = ({ row, handleEdit }) => (
  <BilledCell row={row} handleEdit={handleEdit} />
);

BilledCellWrapper.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
};

export const RemainingCellWrapper = ({ row, handleEdit }) => (
  <RemainingCell row={row} handleEdit={handleEdit} />
);

RemainingCellWrapper.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
};

export const ActionsCellWrapper = ({ row, handleRemoveRow }) => (
  <ActionsCell row={row} handleRemoveRow={handleRemoveRow} />
);

ActionsCellWrapper.propTypes = {
  row: PropTypes.object.isRequired,
  handleRemoveRow: PropTypes.func.isRequired,
};

export const QtyCellWrapper = ({ row, handleEdit }) => (
  <QtyCell row={row} handleEdit={handleEdit} />
);

QtyCellWrapper.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
};

export const UomCellWrapper = ({ row, handleEdit, dropdownOptions, getDisplayText }) => (
  <UomCell
    row={row}
    handleEdit={handleEdit}
    dropdownOptions={dropdownOptions}
    getDisplayText={getDisplayText}
  />
);

UomCellWrapper.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
  dropdownOptions: PropTypes.object.isRequired,
  getDisplayText: PropTypes.func.isRequired,
};

export const UnitCostCellWrapper = ({ row, handleEdit }) => (
  <UnitCostCell row={row} handleEdit={handleEdit} />
);

UnitCostCellWrapper.propTypes = {
  row: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
};
